/**
 * ClientSelector - Component for client selection and creation
 * Must select or create a client before document upload
 */

import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { Client, ClientCreateData } from '../../types/client';
import { Search, Plus, User, Building2, Check, X, AlertCircle } from 'lucide-react';

interface ClientSelectorProps {
  onClientSelected?: (client: Client) => void;
  required?: boolean;
  className?: string;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
  onClientSelected,
  required = true,
  className = ''
}) => {
  const {
    selectedClient,
    setSelectedClient,
    clients,
    isLoadingClients,
    clientError,
    fetchClients,
    createClient,
    searchClients
  } = useOnboarding();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newClient, setNewClient] = useState<ClientCreateData>({
    name: '',
    email: '',
    phone: '',
    type: 'individual',
    jurisdiction: 'CH',
    is_prospect: false  // TASK-178: Mark as prospect
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Handle client selection
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    onClientSelected?.(client);
  };

  // Handle client creation
  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setIsCreating(true);

    try {
      const client = await createClient(newClient);
      if (client) {
        setShowCreateForm(false);
        setNewClient({
          name: '',
          email: '',
          phone: '',
          type: 'individual',
          jurisdiction: 'CH',
          is_prospect: false  // TASK-178: Reset
        });
        handleSelectClient(client);
      } else {
        // createClient swallowed the throw and parked the server's reason on the context's
        // clientError, which is rendered alongside createError below.
        setCreateError(null);
      }
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create client');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery) {
        searchClients(searchQuery);
      } else {
        fetchClients();
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]);

  // Initial load
  useEffect(() => {
    if (clients.length === 0 && !isLoadingClients) {
      fetchClients();
    }
  }, []);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Client Selection {required && <span className="text-red-500">*</span>}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {selectedClient 
                ? `Documents will be uploaded for: ${selectedClient.name}`
                : 'Select an existing client or create a new one before uploading documents'}
            </p>
          </div>
          {selectedClient && (
            <button
              onClick={() => setSelectedClient(null)}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Selected Client Highlight */}
      {selectedClient && !showCreateForm && (
        <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                {selectedClient.classification?.type === 'corporate' ? (
                  <Building2 className="w-5 h-5 text-blue-600" />
                ) : (
                  <User className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <p className="font-semibold text-gray-900">{selectedClient.name}</p>
                  <Check className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-sm text-gray-600">{selectedClient.email}</p>
              </div>
            </div>
            <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded font-medium">
              Currently Selected
            </div>
          </div>
        </div>
      )}

      {/* Search and List - Always Visible */}
      {!showCreateForm && (
        <div className="p-4">
          {/* Search Bar */}
          <div className="flex space-x-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={selectedClient ? "Search to switch client..." : "Search clients by name or email..."}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Client</span>
            </button>
          </div>

          {/* Quick Action Info */}
          {selectedClient && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                💡 Tip: Click on any client below to switch selection
              </p>
            </div>
          )}

          {/* Error Display */}
          {clientError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{clientError}</p>
            </div>
          )}

          {/* Client List */}
          {isLoadingClients ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : clients.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleSelectClient(client)}
                  className={`w-full p-3 text-left border rounded-lg transition-all group ${
                    selectedClient?.id === client.id 
                      ? 'border-blue-500 bg-blue-50 shadow-sm' 
                      : 'border-gray-200 hover:bg-gray-50 hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {client.classification?.type === 'corporate' ? (
                        <Building2 className={`w-5 h-5 ${
                          selectedClient?.id === client.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'
                        }`} />
                      ) : (
                        <User className={`w-5 h-5 ${
                          selectedClient?.id === client.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'
                        }`} />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{client.name}</p>
                        <p className="text-sm text-gray-600">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {client.documents && client.documents.totalDocuments > 0 && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {client.documents.totalDocuments} docs
                        </span>
                      )}
                      {selectedClient?.id === client.id && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No clients found</p>
              <p className="text-sm text-gray-500 mt-1">Create a new client to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Create Client Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateClient} className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Create New Client</h4>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setCreateError(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Display — `createError` covers a thrown failure, `clientError` the reason
              the context captured when createClient returned null. */}
          {(createError || clientError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{createError || clientError}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter client name"
              />
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="client@example.com"
              />
            </div>

            {/* Phone Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="+41 XX XXX XX XX"
              />
            </div>

            {/* Client Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={newClient.type}
                onChange={(e) => setNewClient({ ...newClient, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="individual">Individual</option>
                <option value="corporate">Corporate</option>
                <option value="institutional">Institutional</option>
              </select>
            </div>

            {/* Jurisdiction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jurisdiction
              </label>
              <select
                value={newClient.jurisdiction}
                onChange={(e) => setNewClient({ ...newClient, jurisdiction: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CH">Switzerland</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="IT">Italy</option>
                <option value="AT">Austria</option>
                <option value="LI">Liechtenstein</option>
              </select>
            </div>

            {/* Prospect Checkbox - TASK-178 */}
            <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="is_prospect"
                checked={newClient.is_prospect || false}
                onChange={(e) => setNewClient({ ...newClient, is_prospect: e.target.checked })}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <div className="flex-1">
                <label htmlFor="is_prospect" className="block text-sm font-medium text-gray-900 cursor-pointer">
                  Mark as Prospect
                </label>
                <p className="text-xs text-amber-700 mt-0.5">
                  Check this if the person is not yet a fully onboarded client.
                  Prospects have limited actions until KYC is approved.
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setCreateError(null);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !newClient.name || !newClient.email}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ClientSelector;